import { thumbPath } from "../lib/api";
import type { MediaAsset } from "../lib/types";

/**
 * What a small square draws: the derivative, or something that is not the
 * original (0017-v2 §2).
 *
 * THERE ARE THREE STATES AND THEY ARE NOT THE SAME THING. Collapsing them is
 * how a grid ends up either lying or downloading thirty megabytes:
 *
 *   no derivative   a static grey square. Permanent, and says so by not
 *                   moving — a shimmer that never resolves reads as a bug to
 *                   the person looking at it, and they would be right.
 *   being signed    a shimmer. Temporary, and the movement is the promise that
 *                   something is coming.
 *   signed          the thumbnail.
 *
 * WHAT IT WILL NEVER DO is fall back to `storage_path`. That fallback was the
 * most expensive line in the product — see `thumbPath` — and the whole point of
 * routing every surface through one component is that there is now exactly one
 * place where that temptation could return, rather than five.
 */
export function Thumb({ asset, url, alt }: {
  asset: Pick<MediaAsset, "preview_path" | "mime_type">;
  /** Undefined until `signMedia` comes back. */
  url: string | undefined;
  alt?: string;
}) {
  const video = asset.mime_type.startsWith("video/");

  // No derivative: nothing to draw, and nothing worth spending to draw it.
  // An operator clears these from the portal's thumbnail queue.
  if (thumbPath(asset) === null) {
    return (
      <span className="thumb-fallback" title="No thumbnail yet">
        {video ? "▶" : "🖼"}
      </span>
    );
  }

  if (!url) return <span className="thumb-shimmer" aria-hidden="true" />;

  // A video's derivative is a poster frame, which is a picture. There is no
  // <video> element on a grid any more: the thing that made one necessary was
  // the absence of a poster, and that case now renders as the square above.
  return <img src={url} alt={alt ?? ""} loading="lazy" />;
}
